import {DateTime} from 'luxon';
import {
  FixedPeriodPreset,
  Period,
  PeriodKind,
  PeriodPreset,
  QuarterNumber,
  QuarterPeriodPreset,
} from './types';

export const REPORTING_TIME_ZONE = 'Europe/London';

const FINANCIAL_YEAR_START_MONTH = 4;

type PeriodPresetOption = {
  value: PeriodPreset;
  label: string;
};

export type PeriodPresetGroup = {
  label: string;
  options: PeriodPresetOption[];
};

const FIXED_LABELS: Record<FixedPeriodPreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  last90Days: 'Last 90 days',
  last12Months: 'Last 12 months',
  monthToDate: 'Month to date',
  lastMonth: 'Last month',
  quarterToDate: 'Quarter to date',
  lastQuarter: 'Last quarter',
  yearToDate: 'Year to date',
  lastYear: 'Last year',
  financialYearToDate: 'Financial year to date',
  lastFinancialYear: 'Last financial year',
};

const ROLLING: FixedPeriodPreset[] = [
  'today',
  'yesterday',
  'last7Days',
  'last30Days',
  'last90Days',
  'last12Months',
];

const CALENDAR: FixedPeriodPreset[] = [
  'monthToDate',
  'lastMonth',
  'quarterToDate',
  'lastQuarter',
  'yearToDate',
  'lastYear',
];

const CALENDAR_UNIT_MONTHS: Partial<Record<FixedPeriodPreset, number>> = {
  monthToDate: 1,
  lastMonth: 1,
  quarterToDate: 3,
  lastQuarter: 3,
  yearToDate: 12,
  lastYear: 12,
  financialYearToDate: 12,
  lastFinancialYear: 12,
};

const QUARTER_PATTERN = /^(f?q)([1-4])-(\d{4})$/;

export const DEFAULT_PERIOD_PRESET: FixedPeriodPreset = 'last30Days';

export const toIsoDate = (date: DateTime | null) =>
  date?.isValid ? date.toISODate() : null;

export const reportingToday = () =>
  DateTime.now().setZone(REPORTING_TIME_ZONE).startOf('day');

const isFixedPreset = (value: string): value is FixedPeriodPreset =>
  Object.prototype.hasOwnProperty.call(FIXED_LABELS, value);

const monthName = (month: number) =>
  DateTime.fromObject({month}).toFormat('LLLL');

const FINANCIAL_YEAR_LABEL = `Financial year (${monthName(
  FINANCIAL_YEAR_START_MONTH,
)} to ${monthName(((FINANCIAL_YEAR_START_MONTH + 10) % 12) + 1)})`;

const parseQuarter = (preset: string) => {
  const match = QUARTER_PATTERN.exec(preset);
  if (!match) return null;
  return {
    financial: match[1] === 'fq',
    quarter: Number(match[2]) as QuarterNumber,
    year: Number(match[3]),
  };
};

const financialYearStart = (day: DateTime) => {
  const startYear =
    day.month >= FINANCIAL_YEAR_START_MONTH ? day.year : day.year - 1;
  return day.set({year: startYear, month: FINANCIAL_YEAR_START_MONTH, day: 1});
};

const quarterStart = (financial: boolean, quarter: number, year: number) => {
  const firstMonth = financial ? FINANCIAL_YEAR_START_MONTH : 1;
  return DateTime.fromObject(
    {year, month: firstMonth, day: 1},
    {zone: REPORTING_TIME_ZONE},
  ).plus({months: (quarter - 1) * 3});
};

const lastDayOf = (start: DateTime, months: number) =>
  start.plus({months}).minus({days: 1});

const fixedRange = (
  preset: FixedPeriodPreset,
  today: DateTime,
): [DateTime, DateTime] => {
  switch (preset) {
    case 'today':
      return [today, today];
    case 'yesterday':
      return [today.minus({days: 1}), today.minus({days: 1})];
    case 'last7Days':
      return [today.minus({days: 6}), today];
    case 'last30Days':
      return [today.minus({days: 29}), today];
    case 'last90Days':
      return [today.minus({days: 89}), today];
    case 'last12Months':
      return [today.minus({years: 1}).plus({days: 1}), today];
    case 'monthToDate':
      return [today.startOf('month'), today];
    case 'lastMonth': {
      const start = today.minus({months: 1}).startOf('month');
      return [start, lastDayOf(start, 1)];
    }
    case 'quarterToDate':
      return [today.startOf('quarter'), today];
    case 'lastQuarter': {
      const start = today.minus({quarters: 1}).startOf('quarter');
      return [start, lastDayOf(start, 3)];
    }
    case 'yearToDate':
      return [today.startOf('year'), today];
    case 'lastYear': {
      const start = today.minus({years: 1}).startOf('year');
      return [start, lastDayOf(start, 12)];
    }
    case 'financialYearToDate':
      return [financialYearStart(today), today];
    case 'lastFinancialYear': {
      const start = financialYearStart(today).minus({years: 1});
      return [start, lastDayOf(start, 12)];
    }
  }
};

const presetRange = (
  preset: PeriodPreset,
  today: DateTime,
): [DateTime, DateTime] => {
  if (isFixedPreset(preset)) return fixedRange(preset, today);
  const quarter = parseQuarter(preset);
  if (!quarter) return fixedRange(DEFAULT_PERIOD_PRESET, today);
  const start = quarterStart(quarter.financial, quarter.quarter, quarter.year);
  const end = lastDayOf(start, 3);
  return [start, end > today ? today : end];
};

export const isPeriodPreset = (value: string | null): value is PeriodPreset => {
  if (!value) return false;
  if (isFixedPreset(value)) return true;
  const quarter = parseQuarter(value);
  if (!quarter) return false;
  return (
    quarterStart(quarter.financial, quarter.quarter, quarter.year) <=
    reportingToday()
  );
};

const presetLabel = (preset: PeriodPreset) => {
  if (isFixedPreset(preset)) return FIXED_LABELS[preset];
  const quarter = parseQuarter(preset);
  if (!quarter) return preset;
  if (!quarter.financial) return `Q${quarter.quarter} ${quarter.year}`;
  const endYear = String((quarter.year + 1) % 100).padStart(2, '0');
  return `FQ${quarter.quarter} ${quarter.year}/${endYear}`;
};

export const presetPeriod = (
  preset: PeriodPreset,
  today: DateTime = reportingToday(),
): Period => {
  const [start, end] = presetRange(preset, today);
  return {
    preset,
    startDate: toIsoDate(start) ?? '',
    endDate: toIsoDate(end) ?? '',
  };
};

const startedQuarters = (
  financial: boolean,
  today: DateTime,
): QuarterPeriodPreset[] => {
  const prefix = financial ? 'fq' : 'q';
  const currentYear = financial ? financialYearStart(today).year : today.year;
  return [currentYear - 1, currentYear].flatMap(year =>
    ([1, 2, 3, 4] as const)
      .filter(quarter => quarterStart(financial, quarter, year) <= today)
      .map(quarter => `${prefix}${quarter}-${year}` as QuarterPeriodPreset),
  );
};

const optionsFor = (presets: readonly PeriodPreset[]) =>
  presets.map(value => ({value, label: presetLabel(value)}));

export const periodPresetGroups = (
  today: DateTime = reportingToday(),
): PeriodPresetGroup[] => [
  {label: 'Rolling', options: optionsFor(ROLLING)},
  {label: 'Calendar', options: optionsFor(CALENDAR)},
  {label: 'Quarters', options: optionsFor(startedQuarters(false, today))},
  {
    label: FINANCIAL_YEAR_LABEL,
    options: optionsFor([
      'financialYearToDate',
      'lastFinancialYear',
      ...startedQuarters(true, today),
    ]),
  },
];

export const customPeriod = (
  startDate: string | null,
  endDate: string | null,
): Period | null => {
  if (!startDate || !endDate) return null;
  const start = DateTime.fromFormat(startDate, 'yyyy-MM-dd', {
    zone: REPORTING_TIME_ZONE,
  });
  const end = DateTime.fromFormat(endDate, 'yyyy-MM-dd', {
    zone: REPORTING_TIME_ZONE,
  });
  if (!start.isValid || !end.isValid || start > end) return null;
  if (end > reportingToday()) return null;
  return {preset: 'custom', startDate, endDate};
};

const calendarUnitMonths = (preset: PeriodKind) => {
  if (preset === 'custom') return undefined;
  if (isFixedPreset(preset)) return CALENDAR_UNIT_MONTHS[preset];
  return parseQuarter(preset) ? 3 : undefined;
};

const precedingWindow = (
  start: DateTime,
  end: DateTime,
): [DateTime, DateTime] => {
  const previousEnd = start.minus({days: 1});
  return [previousEnd.minus(end.diff(start, 'days')), previousEnd];
};

const precedingUnit = (
  start: DateTime,
  end: DateTime,
  months: number,
): [DateTime, DateTime] => {
  const previousStart = start.minus({months});
  const previousUnitEnd = lastDayOf(previousStart, months);
  if (end >= lastDayOf(start, months)) return [previousStart, previousUnitEnd];
  const elapsedEnd = previousStart.plus(end.diff(start, 'days'));
  return [
    previousStart,
    elapsedEnd < previousUnitEnd ? elapsedEnd : previousUnitEnd,
  ];
};

export const comparisonPeriod = (period: Period): Period => {
  const start = DateTime.fromISO(period.startDate, {zone: REPORTING_TIME_ZONE});
  const end = DateTime.fromISO(period.endDate, {zone: REPORTING_TIME_ZONE});
  const months = calendarUnitMonths(period.preset);
  const [previousStart, previousEnd] =
    months === undefined
      ? precedingWindow(start, end)
      : precedingUnit(start, end, months);
  return {
    preset: 'custom',
    startDate: toIsoDate(previousStart) ?? period.startDate,
    endDate: toIsoDate(previousEnd) ?? period.endDate,
  };
};

export const periodLabel = (period: Period) =>
  period.preset === 'custom' ? 'Custom range' : presetLabel(period.preset);

export const formatPeriodRange = (period: Period) => {
  const start = DateTime.fromISO(period.startDate);
  const end = DateTime.fromISO(period.endDate);
  const startFormat = start.year === end.year ? 'd MMM' : 'd MMM yyyy';
  return `${start.toFormat(startFormat)} – ${end.toFormat('d MMM yyyy')}`;
};
